import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dropdown, Menu } from 'antd';
import { TbCubeSpark, TbDatabase } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import {
    BaseSkeleton,
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    LearnMoreFooter,
} from '@/shared';
import { useConnectedAccountStore, useReportComponentStore } from '@/stores';
import { ConnectedAccountType, ReportComponent } from '@/types';
import { DataSourceType, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { DatasetSchemaField, useDatasetSchema } from '../../hooks';

interface DataSourceSelectionDropdownProps {
    component: ReportComponent;
    children?: ReactNode;
}

interface ConnectedAccountDataSourceOption {
    description?: string;
    icon: ReactNode;
    key: string;
    label?: string;
    source: 'connectedAccount';
    src?: string;
    type: string;
}

interface VantageDataSourceOption {
    description?: string;
    dimensionsCount?: number;
    icon: ReactNode;
    key: string;
    label?: string;
    metricsCount?: number;
    source: 'vantageDataBlock';
    type: string;
}

type DataSourceOption = ConnectedAccountDataSourceOption | VantageDataSourceOption;

const DataSourceSelectionDropdown: FC<DataSourceSelectionDropdownProps> = ({ component, children }): JSX.Element => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [hasRequestedConnectedAccounts, setHasRequestedConnectedAccounts] = useState(false);
    const [search, setSearch] = useState('');

    const { token } = useAntDesignTheme();
    const { integrationLabelToIconMap } = useMaps();
    const queryClient = useQueryClient();

    const [width, setWidth] = useState<number>();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, []);

    const replaceDraft = useReportComponentStore((s) => s.replaceDraftConfiguration);
    const draftConfigurations = useReportComponentStore((s) => s.draftConfigurations);

    const draft = draftConfigurations[component?.id as string];
    const { connectedAccounts, fetchConnectedAccounts, loading: connectedAccountsLoading } = useConnectedAccountStore();
    const { schema, isLoading } = useDatasetSchema(draft as ReportComponentConfig, { includeCategory: false });

    const isConnectedAccountDatasetId = (key?: string) => String(key ?? '').startsWith('connected-account:');
    const inferConnectedAccountType = (label?: string, description?: string) => {
        const haystack = `${label ?? ''} ${description ?? ''}`;

        return Object.values(ConnectedAccountType).find((type) => haystack.includes(type));
    };
    const parseConnectedAccountResourceDatasetId = (key?: string) => {
        const [prefix, providerId, datasetKey, ...rest] = String(key ?? '')
            .split(':')
            .map((segment) => decodeURIComponent(segment));

        if (prefix !== 'connected-account' || !providerId || datasetKey !== 'resources' || rest.length) {
            return undefined;
        }

        return providerId;
    };

    const options = useMemo(() => {
        const providerOptionsFromSchema = (schema?.datasets ?? [])
            .map((dataset: DatasetSchemaField): ConnectedAccountDataSourceOption | undefined => {
                const providerId = parseConnectedAccountResourceDatasetId(dataset.key);

                if (!providerId) {
                    return undefined;
                }

                const connectedAccount = connectedAccounts.find((account) => account.id === providerId);
                const type = connectedAccount?.type ?? inferConnectedAccountType(dataset.label, dataset.description);

                return {
                    key: providerId,
                    label: dataset.label,
                    description: dataset.description,
                    type: type ?? dataset.label ?? 'Connected Account',
                    source: 'connectedAccount' as const,
                    src: type ? integrationLabelToIconMap[type as ConnectedAccountType] : undefined,
                    icon: <TbDatabase size='75%' style={{ color: token.colorTextTertiary }} />,
                };
            })
            .filter((option): option is ConnectedAccountDataSourceOption => Boolean(option));
        const dataBlockOptions = (schema?.datasets ?? [])
            .filter((dataBlock: DatasetSchemaField) => !isConnectedAccountDatasetId(dataBlock.key))
            .map((dataBlock: DatasetSchemaField): VantageDataSourceOption => ({
                key: dataBlock.key,
                label: dataBlock.label,
                description: dataBlock.description,
                metricsCount: dataBlock.metricsCount,
                dimensionsCount: dataBlock.dimensionsCount,
                source: 'vantageDataBlock' as const,
                type: 'Vantage Datablock',
                icon: <TbCubeSpark size='75%' style={{ color: token.colorTextTertiary }} />,
            }));
        const providerOptionById = new Map<string, DataSourceOption>();

        providerOptionsFromSchema.forEach((option) => {
            if (option.key) {
                providerOptionById.set(option.key, option);
            }
        });

        return [...providerOptionById.values(), ...dataBlockOptions];
    }, [connectedAccounts, integrationLabelToIconMap, schema?.datasets, token.colorTextTertiary]);
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const filteredOptions = normalizedSearch
        ? options.filter((option) =>
              [option?.label, option?.description, option?.type, option?.key].some((field) =>
                  String(field ?? '')
                      .toLocaleLowerCase()
                      .includes(normalizedSearch),
              ),
          )
        : options;

    const showOptionsLoading = isLoading || (connectedAccountsLoading && connectedAccounts.length === 0);

    const handleDropdownOpenChange = (nextOpen: boolean) => {
        setDropdownOpen(nextOpen);

        if (!nextOpen) {
            setSearch('');
        }

        if (nextOpen && !hasRequestedConnectedAccounts && connectedAccounts.length === 0) {
            setHasRequestedConnectedAccounts(true);
            void fetchConnectedAccounts();
        }
    };

    return (
        <Dropdown
            open={dropdownOpen}
            onOpenChange={handleDropdownOpenChange}
            trigger={['click']}
            overlayStyle={{ width }}
            popupRender={() => (
                <DropdownPanel
                    footer={<LearnMoreFooter topic='our data' variant='dropdown' />}
                    search={{
                        placeholder: 'Filter Data Sources...',
                        setValue: setSearch,
                        value: search,
                    }}
                    style={{ marginTop: '0.25rem' }}
                    width={width}
                >
                    <DropdownMenuLabel>
                        {filteredOptions.length} Option{filteredOptions.length === 1 ? '' : 's'}
                    </DropdownMenuLabel>
                    <DropdownPanelMenu selectable={false}>
                        {showOptionsLoading ? (
                            <Menu.Item disabled key='loading-data-sources'>
                                <BaseSkeleton title={false} paragraph={{ rows: 3 }} />
                            </Menu.Item>
                        ) : filteredOptions.length !== 0 ? (
                            filteredOptions.map((o) => {
                                return (
                                    <Menu.Item
                                        key={o?.key}
                                        onClick={() => {
                                            const baseDraft = (draft ??
                                                component.configuration ??
                                                {}) as Partial<ReportComponentConfig>;

                                            if (o.source === 'vantageDataBlock') {
                                                if (draft?.dataSet?.key === o.key) {
                                                    return;
                                                }

                                                replaceDraft(component.id, {
                                                    ...baseDraft,
                                                    data: undefined,
                                                    dataSet: {
                                                        type: DataSourceType.THIRD_PARTY,
                                                        key: o.key,
                                                    },
                                                    query: {
                                                        datasetId: o.key,
                                                        metrics: [],
                                                        dimensions: [],
                                                    },
                                                });

                                                return;
                                            }

                                            if (draft?.dataSet?.providerId === o.key && !draft?.dataSet?.key) {
                                                return;
                                            }

                                            void queryClient.invalidateQueries({
                                                queryKey: ['datasetSchema', 2, DataSourceType.THIRD_PARTY, null, o.key],
                                            });

                                            void queryClient.prefetchQuery({
                                                queryKey: ['datasetSchema', 2, DataSourceType.THIRD_PARTY, null, o.key],
                                                queryFn: async () => {
                                                    const response = await authorizedAxiosInstance.get(
                                                        '/api/datasets/schema',
                                                        {
                                                            params: {
                                                                type: DataSourceType.THIRD_PARTY,
                                                                providerId: o.key,
                                                            },
                                                        },
                                                    );

                                                    return response.data;
                                                },
                                            });

                                            replaceDraft(component.id, {
                                                ...baseDraft,
                                                data: undefined,
                                                dataSet: {
                                                    type: DataSourceType.THIRD_PARTY,
                                                    providerId: o.key,
                                                },
                                                query: {
                                                    datasetId: '',
                                                    metrics: [],
                                                    dimensions: [],
                                                },
                                            });
                                        }}
                                    >
                                        <DropdownOptionContent
                                            avatar={{
                                                icon: o.icon,
                                                src: o.source === 'connectedAccount' ? o.src : undefined,
                                            }}
                                            title={o?.label}
                                            metadata={
                                                o.source === 'vantageDataBlock'
                                                    ? [
                                                          `${o.metricsCount ?? 0} Metrics`,
                                                          `${o.dimensionsCount ?? 0} Dimensions`,
                                                      ]
                                                    : [o?.type, o?.description]
                                            }
                                            selected={
                                                draft?.dataSet?.key === o?.key ||
                                                (o.source === 'connectedAccount' &&
                                                    draft?.dataSet?.providerId === o?.key &&
                                                    !draft?.dataSet?.key)
                                            }
                                        />
                                    </Menu.Item>
                                );
                            })
                        ) : (
                            <EmptySection
                                height='12.5rem'
                                icon={<TbDatabase size='75%' style={{ color: token.colorTextTertiary }} />}
                                label='No Data Sources'
                                description={
                                    search
                                        ? 'No data sources match your search.'
                                        : "You don't have any data sources connected."
                                }
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <div ref={ref}>
                <span>{children}</span>
            </div>
        </Dropdown>
    );
};

export default DataSourceSelectionDropdown;
